// Shared B2B / B2C "lead type" vocabulary for relay.contacts and
// relay.companies. The DB CHECK constraints on both tables' `lead_type` column
// mirror LEAD_TYPES exactly (see
// supabase/migrations/20260607120000_relay_lead_type.sql).
//
// lead_type is orthogonal to the lifecycle "stage" (see @/lib/stages): stage is
// where a lead sits in the funnel; lead_type is what kind of lead it is.
//   b2c — an individual person is the customer (the contact IS the account)
//   b2b — a business is the lead; people are contacts under the company

export const LEAD_TYPES = ["b2b", "b2c"] as const;

export type LeadType = (typeof LEAD_TYPES)[number];

// Per-entity defaults: a bare contact is a person, a company is a business.
export const DEFAULT_CONTACT_LEAD_TYPE: LeadType = "b2c";
export const DEFAULT_COMPANY_LEAD_TYPE: LeadType = "b2b";

/** Human-readable label for each lead type (selectors, badges, read views). */
export const LEAD_TYPE_LABELS: Record<LeadType, string> = {
  b2b: "B2B — business",
  b2c: "B2C — individual",
};

/** Short label for compact contexts (filter chips, table cells). */
export const LEAD_TYPE_SHORT_LABELS: Record<LeadType, string> = {
  b2b: "B2B",
  b2c: "B2C",
};

/** Tooltip / helper copy explaining what each lead type means. */
export const LEAD_TYPE_DEFINITIONS: Record<LeadType, string> = {
  b2b: "A business is the lead; people are contacts under the company.",
  b2c: "An individual person is the customer.",
};

/** Safe label lookup for values that may not be a known lead type. */
export function leadTypeLabel(value: string): string {
  return (LEAD_TYPE_LABELS as Record<string, string>)[value] ?? value;
}

/** Safe short-label lookup for values that may not be a known lead type. */
export function leadTypeShortLabel(value: string): string {
  return (LEAD_TYPE_SHORT_LABELS as Record<string, string>)[value] ?? value;
}
